import { Component, OnInit } from '@angular/core';
import { SharedComponentsModule } from "../../../../shared/module/shared-components.module";
import { firstValueFrom, Subject } from "rxjs";
import { AuthService } from "../../../../auth/service/auth.service";
import { OrganizationsService } from "../../../../organization/components/service/organizations.service";
import { NOTIFICATIONS, NotificationsService } from "../../../../../core/service/notifications.service";
import { filter, switchMap, takeUntil, tap } from "rxjs/operators";
import {
  CompanyCrossCompanyDTO,
  CrossCompanyResponse,
  KpiCrossCompanyService, MetricCrossCompanyDTO, MetricDataCrossCompanyDTO,
} from "./service/kpi-cross-company.service";
import { Organization } from '../../../../organization/dto/Organization';
import { MetricDataRole } from "../../../dto/MetricData";
import { MetricDataType } from "../../../../recovery/component/recovery-metrics-panel/entity/DefaultMetric";
import { Metric } from "../../../dto/Metric";
import { Clipboard } from "@angular/cdk/clipboard";
import { KpiCrossCompanyCrudComponent } from "./kpi-cross-company-crud/kpi-cross-company-crud.component";
import { KpiCrossCompanyChartComponent } from "./kpi-cross-company-chart/kpi-cross-company-chart.component";
import { KpiCrossCompanyHelperService } from "./service/kpi-cross-company-helper.service";
import { ExcelService } from "../../../../excel";
import { ExcelTemplateName } from "../../../../excel/interfaces";
import { LocalDate } from "../../../../../core/pojo/date-variable";
import { KpiDocumentsToCount } from "../../../../../util/pipes/kpi-documents-to-count.pipe";
import {
  CloseKpiMetricDocumentModalUpdateOutput,
  KpiModalDocumentCrudComponent,
} from "../../../modals/kpi-modal-document-crud/kpi-modal-document-crud.component";
import { KpiModalDocumentCrudWrapper } from "../../../dto/box/box-kpi-dtos";
import {
  TooltipCrossCompanyTableDocumentTableSettings,
} from "../../../service/internal/documents-helper.service";
import { getKpiCompanyMetricsUrl } from "../../../module/kpi-module-routing.module";
import { ActivatedRoute, Router } from "@angular/router";

@Component({
  selector: 'app-cross-company',
  standalone: true,
  imports: [
    SharedComponentsModule,
    KpiCrossCompanyCrudComponent,
    KpiDocumentsToCount,
    KpiModalDocumentCrudComponent,
    KpiCrossCompanyChartComponent,
  ],
  templateUrl: './kpi-cross-company.component.html',
  styleUrl: './kpi-cross-company.component.scss',
})
export class KpiCrossCompanyComponent implements OnInit {
  private _destroyStream = new Subject<void>();

  protected readonly MetricDataRole = MetricDataRole;
  protected readonly MetricDataType = MetricDataType;
  protected readonly Number = Number;
  protected readonly Metric = Metric;

  selectedOrg: Organization;
  isAdmin: boolean;
  rawCrossCompanyData: CrossCompanyResponse;
  crossCompanyData: CrossCompanyResponse;
  showCrudModal: boolean = false;
  showChartModal: boolean = false;
  isLoading = true;
  chartMetric: MetricCrossCompanyDTO;

  documentCrudWrapper: MetricDocumentUpdate = {
    wrapper: null,
    metricData: null,
  };

  tooltipDoc: TooltipCrossCompanyTableDocumentTableSettings = {
    show: false,
  };

  constructor(private authService: AuthService,
    private orgService: OrganizationsService,
    private notificationService: NotificationsService,
    private kpiCrossCompanyService: KpiCrossCompanyService,
    private helperService: KpiCrossCompanyHelperService,
    private router: Router,
    private route: ActivatedRoute,
    private clipboard: Clipboard,
    private excelService: ExcelService,
  ) {
  }

  ngOnInit(): void {
    this.initSubscriptions();
  }

  flipMetricVisibility(metric: MetricCrossCompanyDTO): void {
    metric.showCompanies = !metric.showCompanies;
  }

  copyToClipboard(str: string): void {
    this.clipboard.copy(str);
    this.notificationService.success(NOTIFICATIONS.COPIED_NO_TITLE);
  }

  openChartModal(metric: MetricCrossCompanyDTO): void {
    this.chartMetric = this.rawCrossCompanyData.metrics.find((dto) => dto.name === metric.name && dto.groupName === metric.groupName);
    this.showChartModal = true;
  }

  closeChartModal(): void {
    this.chartMetric = null;
    this.showChartModal = false;
  }

  updateRowValueFromMetricDocumentAndClose(output: CloseKpiMetricDocumentModalUpdateOutput): void {
    this.crossCompanyData.metrics.forEach((metric) => {
      metric.companies.forEach((company) => {
        const targetDataIndex =
          company.data.findIndex((data) =>
            data.uuid === this.documentCrudWrapper.metricData.uuid);
        if (targetDataIndex !== -1) {
          company.data[targetDataIndex].rowValue = output.rowValue;
        }
      });
    });
    if (output.close) {
      this.documentCrudWrapper.wrapper = null;
      this.documentCrudWrapper.metricData = null;
    }
  }

  showDocuments(data: MetricDataCrossCompanyDTO, metric: MetricCrossCompanyDTO,
    companyUUID: string): void {
    const boxFolderId = data.rowValue.split('\n')[0];
    this.documentCrudWrapper.metricData = data;
    this.documentCrudWrapper.wrapper = new KpiModalDocumentCrudWrapper(
      companyUUID,
      data.uuid,
      boxFolderId,
      data.period,
      metric.name,
      false,
      false,
    );
  }

  activateDocumentsToolTip(id: string, text: string): void {
    this.tooltipDoc = {
      show: true,
      selectedFiles: text.split('\n').slice(1),
      title: text,
      coordinates: document.getElementById(id).getBoundingClientRect(),
    };
  }

  hideDocumentTooltip(): void {
    this.tooltipDoc = {
      show: false,
      selectedFiles: [],
    };
  }

  async closeCrudModal(isSuccessful: boolean): Promise<void> {
    if (isSuccessful) {
      await this.getData();
    }
    this.showCrudModal = false;
  }

  async goToCompanyMetrics(company: CompanyCrossCompanyDTO): Promise<void> {
    await this.router.navigate(
      [getKpiCompanyMetricsUrl(company.companyUUID)],
      { queryParams: this.route.snapshot.queryParams });
  }

  exportTable(): void {
    const fileName = this.selectedOrg.name + '_kpi_cross_company.xlsx';
    const exportData: MetricCrossCompanyExportExcel = {
      periods: this.crossCompanyData.periods,
      metrics: this.crossCompanyData.metrics,
    };
    this.excelService.exportByTemplate(exportData, fileName, ExcelTemplateName.KpiCrossCompany);
  }

  private initSubscriptions(): void {
    this.authService.currentUserTypeForServices$.pipe(takeUntil(this._destroyStream))
      .subscribe((userType) => {
        if (!userType) {
          return;
        }
        this.isAdmin = userType === 'admin';
      });

    this.orgService.selected$
      .pipe(
        filter((org) => Boolean(org)),
        tap((org) => this.selectedOrg = org),
        switchMap(() => this.getData()),
        takeUntil(this._destroyStream))
      .subscribe();
  }

  private async getData(): Promise<void> {
    this.isLoading = true;
    this.rawCrossCompanyData = await firstValueFrom(this.kpiCrossCompanyService.getData(this.selectedOrg.uuid));
    this.crossCompanyData = this.helperService.enrichResponse(this.rawCrossCompanyData);
    this.isLoading = false;
  }
}

interface MetricDocumentUpdate {
  wrapper: KpiModalDocumentCrudWrapper;
  metricData: MetricDataCrossCompanyDTO;
}


export interface MetricCrossCompanyExportExcel {
  periods: LocalDate[];
  metrics: MetricCrossCompanyDTO[];
}

